<script lang="ts" setup>
import BaseModal from "@/components/base/BaseModal.vue";
import BaseProgressBar from "@/components/base/BaseProgressBar.vue";

defineProps<{
  activeNumber?: number;
  activeNumberPrice?: number;
  activeNumberType: string;
  errorModal: boolean;
  errorSixMonthsModal: boolean;
  formatPhone: (phone: string) => string;
  isLoadingChangeNumber: boolean;
  openModal: boolean;
  openModalFirstPayment: boolean;
  openModalLowMoney: boolean;
  openModalSecondPayment: boolean;
  openModalTwoParts: boolean;
  successModal: boolean;
  walletAmount?: number;
  walletAmountCalc?: number;
}>();

const emit = defineEmits<{
  (e: "close-confirm"): void;
  (e: "close-error"): void;
  (e: "close-first-payment"): void;
  (e: "close-low-money"): void;
  (e: "close-second-payment"): void;
  (e: "close-six-months-error"): void;
  (e: "close-success"): void;
  (e: "close-two-parts"): void;
  (e: "confirm", number: number, type: string): void;
  (e: "continue-to-first-payment"): void;
  (e: "continue-to-second-payment"): void;
  (e: "to-payment"): void;
}>();
</script>

<template>
  <Teleport to=".root">
    <BaseModal
      v-if="openModal"
      :title="$t('premium.modals.confirmTitle')"
      :main-btn-title="$t('premium.modals.confirmAction')"
      :mainBtnLoading="isLoadingChangeNumber"
      @action="emit('confirm', activeNumber as number, activeNumberType)"
      @close="emit('close-confirm')"
    >
      <div class="premium-phones--modal">
        <div class="premium-phones--modal-title">
          <p class="footnote color-text-primary">
            {{ $t("premium.modals.selectedNumber") }}
          </p>
          <p class="h4 color-text-primary">
            {{ formatPhone(String(activeNumber)) }}
          </p>
        </div>
        <div class="premium-phones--modal-price">
          <p class="caption-navbar color-text-primary">
            {{ activeNumberPrice }} ₽
          </p>
        </div>
      </div>
    </BaseModal>

    <BaseModal
      v-if="openModalLowMoney"
      :title="$t('premium.modals.lowMoneyTitle')"
      :main-btn-title="
        $t('premium.modals.topUpOn', {
          amount: (activeNumberPrice || 0) - (walletAmountCalc || 0),
        })
      "
      @action="emit('to-payment')"
      @close="emit('close-low-money')"
    >
      <div class="premium-phones--modal">
        <div class="premium-phones--modal-title">
          <p class="footnote color-text-primary">
            {{ $t("premium.modals.yourBalance") }}
          </p>
          <p class="h4 color-text-primary">
            {{ walletAmountCalc }}₽ /
            <span class="color-grey-light">{{ activeNumberPrice }}₽</span>
          </p>
          <div class="premium-phones--modal-progress">
            <BaseProgressBar
              :current-amount="walletAmount as number"
              :max-amount="activeNumberPrice as number"
            />
          </div>
        </div>
      </div>
    </BaseModal>

    <BaseModal
      v-if="openModalTwoParts"
      :title="$t('premium.modals.twoPartsTitle')"
      :main-btn-title="$t('common.actions.continue')"
      :text="$t('premium.modals.twoPartsText')"
      @action="emit('continue-to-first-payment')"
      @close="emit('close-two-parts')"
    />

    <BaseModal
      v-if="openModalFirstPayment"
      :title="$t('premium.modals.firstPaymentTitle')"
      :main-btn-title="$t('premium.modals.firstPaymentAction')"
      @action="emit('continue-to-second-payment')"
      @close="emit('close-first-payment')"
    >
      <div class="premium-phones--modal">
        <div class="premium-phones--modal-title">
          <p class="footnote color-text-primary">
            {{ $t("premium.modals.yourBalance") }}
          </p>
          <p class="h4 color-text-primary">
            {{ walletAmount }}₽ /
            <span class="color-grey-light">{{ activeNumberPrice }}₽</span>
          </p>
          <div class="premium-phones--modal-progress">
            <BaseProgressBar
              :current-amount="walletAmount as number"
              :max-amount="activeNumberPrice as number"
            />
          </div>
        </div>
      </div>
    </BaseModal>

    <BaseModal
      v-if="openModalSecondPayment"
      :title="$t('premium.modals.secondPaymentTitle')"
      :main-btn-title="
        $t('premium.modals.topUpOn', {
          amount: (activeNumberPrice || 0) - (walletAmount || 0),
        })
      "
      @action="emit('to-payment')"
      @close="emit('close-second-payment')"
    >
      <div class="premium-phones--modal">
        <div class="premium-phones--modal-title">
          <p class="footnote color-text-primary">
            {{ $t("premium.modals.yourBalance") }}
          </p>
          <p class="h4 color-text-primary">
            {{ walletAmount }}₽ /
            <span class="color-grey-light">{{ activeNumberPrice }}₽</span>
          </p>
          <div class="premium-phones--modal-progress">
            <BaseProgressBar
              :current-amount="walletAmount as number"
              :max-amount="activeNumberPrice as number"
            />
          </div>
        </div>
      </div>
    </BaseModal>

    <BaseModal
      v-if="successModal"
      :title="$t('premium.modals.successTitle')"
      :main-btn-title="$t('common.actions.ok')"
      :is-success-modal="true"
      @action="emit('close-success')"
      @close="emit('close-success')"
    />

    <BaseModal
      v-if="errorModal"
      :title="$t('premium.modals.errorBusyTitle')"
      :main-btn-title="$t('common.actions.continue')"
      @action="emit('close-error')"
      @close="emit('close-error')"
    />

    <BaseModal
      v-if="errorSixMonthsModal"
      :title="$t('premium.modals.errorSixMonthsTitle')"
      :main-btn-title="$t('common.actions.continue')"
      @action="emit('close-six-months-error')"
      @close="emit('close-six-months-error')"
    />
  </Teleport>
</template>

<style lang="scss" scoped>
@import "@/assets/style/components/premium";
</style>
